import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../services/api';
import { compressImages } from '../utils/imageCompression';
import { limitFilesForUpload } from '../utils/uploadLimits';

const ChatContainer = styled(Paper)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(1),
}));

const MessageBubble = styled(Paper)(({ theme, isUser }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1),
  maxWidth: '70%',
  marginLeft: isUser ? 'auto' : 0,
  marginRight: isUser ? 0 : 'auto',
  backgroundColor: isUser 
    ? theme.palette.primary.main 
    : theme.palette.mode === 'dark' 
      ? theme.palette.grey[800] 
      : theme.palette.grey[100],
  color: isUser 
    ? theme.palette.primary.contrastText 
    : theme.palette.text.primary,
}));

const InputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'flex-end',
  gap: theme.spacing(1),
}));

const FileInput = styled('input')({
  display: 'none',
});

const MarkdownContent = styled(Box)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  lineHeight: 1.5,
  '& > :first-of-type': {
    marginTop: 0,
  },
  '& > :last-child': {
    marginBottom: 0,
  },
  '& p': {
    margin: 0,
    marginBottom: theme.spacing(1),
    lineHeight: 1.5,
  },
  '& ul, & ol': {
    margin: 0,
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(3),
  },
  '& li': {
    marginBottom: theme.spacing(0.5),
  },
  '& strong': {
    fontWeight: 700,
  },
  '& code': {
    padding: '0 4px',
    borderRadius: 4,
    fontSize: '0.85em',
    fontFamily: 'monospace',
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.06)',
  },
  '& pre': {
    margin: 0,
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1.5),
    overflowX: 'auto',
    borderRadius: 8,
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.06)'
      : 'rgba(0, 0, 0, 0.04)',
  },
  '& pre code': {
    padding: 0,
    backgroundColor: 'transparent',
  },
}));

const AIAssistantChat = ({ 
  onClose, 
  isEmbedded = false, 
  conversationId = 'default', 
  onNewConversation,
  initialMessages = null,
  loading: externalLoading = false
}) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm your AI assistant. I can help you with questions about your projects, data, and CRM system. You can also upload images and PDFs for me to analyze. Additionally, I can create new forms, users, projects, and quotes, etc. for you. How can I assist you today?",
      isUser: false,
      timestamp: new Date(),
    }
  ]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(conversationId);
  
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Update messages when initialMessages change (conversation switching)
  useEffect(() => {
    if (initialMessages && Array.isArray(initialMessages)) {
      setMessages(initialMessages);
    } else if (initialMessages === null) {
      // Reset to default greeting for new conversations
      setMessages([
        {
          id: 1,
          text: "Hello! I'm your AI assistant. I can help you with questions about your projects, data, and CRM system. You can also upload images and PDFs for me to analyze. Additionally, I can create new forms, users, projects, and quotes, etc. for you. How can I assist you today?",
          isUser: false,
          timestamp: new Date(),
        }
      ]);
    }
  }, [initialMessages]);

  // Update current conversation ID when prop changes
  useEffect(() => {
    if (conversationId && conversationId !== currentConversationId) {
      setCurrentConversationId(conversationId);
    }
  }, [conversationId, currentConversationId]);

  const handleSendMessage = async () => {
    if (!currentMessage.trim() && attachedFiles.length === 0) return;

    const userMessage = {
      id: Date.now(),
      text: currentMessage,
      isUser: true,
      timestamp: new Date(),
      files: attachedFiles.length > 0 ? [...attachedFiles] : undefined,
    };

    setMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setIsLoading(true);
    setError(null);

    try {
      let response;
      
      if (attachedFiles.length > 0) {
        // Send message with files
        const formData = new FormData();
        formData.append('message', currentMessage);
        formData.append('conversation_id', currentConversationId);
        
        attachedFiles.forEach((file, index) => {
          formData.append('files', file);
        });

        response = await api.assistantApi.chatWithFiles(formData);
        setAttachedFiles([]); // Clear attached files after sending
      } else {
        // Send text-only message
        response = await api.assistantApi.chat(currentMessage, currentConversationId);
      }

      const botMessage = {
        id: Date.now() + 1,
        text: response.data.response,
        isUser: false,
        timestamp: new Date(),
        files_processed: response.data.files_processed,
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      setError(error.response?.data?.error || 'Failed to send message');
      
      const errorMessage = {
        id: Date.now() + 1,
        text: "I apologize, but I'm having trouble responding right now. Please try again in a moment.",
        isUser: false,
        timestamp: new Date(),
        isError: true,
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileSelect = async (event) => {
    const { files: selectedFiles, warning } = limitFilesForUpload(event.target.files);
    if (warning) {
      setError(warning);
      return;
    }

    const validFiles = selectedFiles.filter(file => {
      const fileType = file.type.toLowerCase();
      const fileName = file.name.toLowerCase();
      return (
        fileType.startsWith('image/') || 
        fileType === 'application/pdf' ||
        fileName.endsWith('.pdf')
      );
    });

    if (validFiles.length !== selectedFiles.length) {
      setError('Some files were skipped. Only images and PDFs are supported.');
    }

    // Compress images (PDFs will pass through unchanged)
    const processedFiles = await compressImages(validFiles);

    setAttachedFiles(prev => [...prev, ...processedFiles]);
    
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachedFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const startNewChat = async () => {
    try {
      // Call the parent's new conversation handler to generate new UUID
      if (onNewConversation) {
        onNewConversation();
      }
      
      // Reset the frontend state
      setMessages([
        {
          id: 1,
          text: "Hello! I'm your AI assistant. How can I help you today?",
          isUser: false,
          timestamp: new Date(),
        }
      ]);
      setCurrentMessage('');
      setAttachedFiles([]);
      setError(null);
    } catch (error) {
      console.error('Error starting new conversation:', error);
      setError('Failed to start new conversation');
    }
  };

  // Show loading state from parent (conversation switching) or local loading
  const showLoading = externalLoading || isLoading;

  const normalizeMessageText = (value) => {
    if (typeof value === 'string') {
      return value;
    }

    if (value == null) {
      return '';
    }

    if (React.isValidElement(value)) {
      const { children } = value.props || {};
      if (typeof children === 'string') {
        return children;
      }
      if (Array.isArray(children)) {
        return children
          .map(child => (typeof child === 'string' ? child : ''))
          .join('');
      }
      return '';
    }

    if (Array.isArray(value)) {
      return value.map(item => normalizeMessageText(item)).join('');
    }

    if (typeof value === 'object') {
      if (typeof value.text === 'string') {
        return value.text;
      }
      if (typeof value.content === 'string') {
        return value.content;
      }

      try {
        return JSON.stringify(value);
      } catch (error) {
        return '';
      }
    }

    return String(value);
  };

  const renderMessageContent = (message) => {
    const messageText = normalizeMessageText(message.text);

    if (message.isUser) {
      return (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {messageText}
        </Typography>
      );
    }

    return (
      <MarkdownContent>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {messageText}
        </ReactMarkdown>
      </MarkdownContent>
    );
  };

  return (
    <ChatContainer elevation={isEmbedded ? 3 : 1}>
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
            <BotIcon />
          </Avatar>
          <Typography variant="h6">AI Assistant</Typography>
        </Box>
        <Box>
          <Tooltip title="New Chat">
            <IconButton onClick={startNewChat} size="small">
              <AddIcon />
            </IconButton>
          </Tooltip>
          {onClose && (
            <Tooltip title="Close">
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)}
          sx={{ m: 1 }}
        >
          {error}
        </Alert>
      )}

      {/* Messages */}
      <MessagesContainer>
        <List>
          {messages.map((message) => (
            <ListItem key={message.id} sx={{ px: 0, py: 0.5 }}>
              <Box sx={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Avatar sx={{ 
                  bgcolor: message.isUser 
                    ? 'primary.main' 
                    : (theme) => theme.palette.mode === 'dark' 
                      ? 'grey.700' 
                      : 'grey.300',
                  width: 24,
                  height: 24,
                  mt: 0.5
                }}>
                  {message.isUser ? <PersonIcon fontSize="small" /> : <BotIcon fontSize="small" />}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <MessageBubble elevation={1} isUser={message.isUser}>
                    {renderMessageContent(message)}
                    {message.files && message.files.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        {message.files.map((file, index) => (
                          <Chip
                            key={index}
                            label={file.name}
                            size="small"
                            sx={{ mr: 1, mb: 1 }}
                          />
                        ))}
                      </Box>
                    )}
                    {message.files_processed && message.files_processed.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Processed files:
                        </Typography>
                        {message.files_processed.map((file, index) => (
                          <Chip
                            key={index}
                            label={`${file.filename} (${file.type})`}
                            size="small"
                            sx={{ mr: 1, mb: 1 }}
                          />
                        ))}
                      </Box>
                    )}
                  </MessageBubble>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {message.timestamp.toLocaleTimeString()}
                  </Typography>
                </Box>
              </Box>
            </ListItem>
          ))}
          {showLoading && (
            <ListItem sx={{ px: 0, py: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ 
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.300', 
                  width: 24, 
                  height: 24 
                }}>
                  <BotIcon fontSize="small" />
                </Avatar>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  {externalLoading ? 'Loading conversation...' : 'Thinking... This may take a few minutes for more complex tasks.'}
                </Typography>
              </Box>
            </ListItem>
          )}
        </List>
        <div ref={messagesEndRef} />
      </MessagesContainer>

      {/* Attached Files Preview */}
      {attachedFiles.length > 0 && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Attached Files:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {attachedFiles.map((file, index) => (
              <Chip
                key={index}
                label={file.name}
                onDelete={() => removeAttachedFile(index)}
                size="small"
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Input */}
      <InputContainer>
        <FileInput
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          multiple
          accept="image/*,.pdf"
        />
        <Tooltip title="Attach Images or PDFs">
          <IconButton 
            onClick={() => fileInputRef.current?.click()}
            disabled={showLoading}
          >
            <AttachFileIcon />
          </IconButton>
        </Tooltip>
        <TextField
          multiline
          maxRows={4}
          value={currentMessage}
          onChange={(e) => setCurrentMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your message..."
          variant="outlined"
          size="small"
          disabled={showLoading}
          sx={{ flex: 1 }}
        />
        <IconButton 
          onClick={handleSendMessage}
          disabled={showLoading || (!currentMessage.trim() && attachedFiles.length === 0)}
          color="primary"
        >
          <SendIcon />
        </IconButton>
      </InputContainer>
    </ChatContainer>
  );
};

export default AIAssistantChat;
